import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faCheck,
  faStore,
  faSpinner,
  faLocationCrosshairs,
  faCopy,
  faUpload,
  faUniversity,
  faMobileAlt,
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config/api.config';
import { BANK_ACCOUNT, WALLET_ACCOUNT, PAYMENT_CURRENCY } from '../config/payment.config';
import { openWhatsAppLink } from '../config/whatsapp.config';
import { useToast } from './Toast';
import BackButton from './BackButton';
import LoginPrompt from './LoginPrompt';
import { getStoredUser } from '../utils/guestStore';
import './CheckoutPage.css';

const formatMoney = (amount) => {
  const n = Number(amount);
  const value = Number.isFinite(n) ? n.toFixed(2) : String(amount);
  return `Rs. ${value}`;
};

const copyText = async (label, value, toast) => {
  if (!value) {
    toast.warning(`${label} is not set yet`);
    return;
  }
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`Copied ${label}`);
  } catch {
    toast.info(value);
  }
};

const reverseGeocode = async (lat, lng) => {
  const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Unable to resolve address');
  const data = await res.json();
  const parts = [
    data.locality || data.city || '',
    data.principalSubdivision || '',
    data.countryName || '',
  ].filter(Boolean);
  const line = data.localityInfo?.informative?.[0]?.name
    || data.localityInfo?.administrative?.[0]?.name
    || '';
  const streetish = [line, data.locality || data.city].filter(Boolean).join(', ');
  return {
    address: streetish || parts.join(', ') || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
    city: data.city || data.locality || data.principalSubdivision || '',
  };
};

const CheckoutPage = ({ cart, total, shippingMethod: shippingMethodProp, onBackClick, onSuccess, onLoginClick }) => {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [user, setUser] = useState(null);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [paymentProof, setPaymentProof] = useState('');
  const [proofPreview, setProofPreview] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locating, setLocating] = useState(false);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [placedOrderId, setPlacedOrderId] = useState(null);
  const [placedPaymentStatus, setPlacedPaymentStatus] = useState('');
  const [whatsappLinks, setWhatsappLinks] = useState({ admin: '', customerNotify: '' });
  const mapRef = useRef(null);
  const proofInputRef = useRef(null);

  const shippingMethod =
    shippingMethodProp || localStorage.getItem('shippingMethod') || 'delivery';
  const isPickup = shippingMethod === 'pickup';

  useEffect(() => {
    const userData = getStoredUser();
    if (userData) {
      setUser(userData);
      setPhone(userData.phone || '');
    }
    setLoading(false);
  }, []);

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setLatitude(lat);
        setLongitude(lng);
        try {
          const geo = await reverseGeocode(lat, lng);
          setAddress(geo.address);
          if (geo.city) setCity(geo.city);
          toast.success('Current location address loaded');
        } catch (err) {
          toast.error('Could not resolve street address. Please type it manually.');
        } finally {
          setLocating(false);
        }
      },
      (error) => {
        setLocating(false);
        toast.error(error.message || 'Unable to get your location');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleStep1Continue = () => {
    if (!phone.trim()) {
      toast.warning('Please enter your phone number');
      return;
    }
    setStep(isPickup ? 3 : 2);
  };

  const handleStep2Continue = () => {
    if (!address.trim()) {
      toast.warning('Please enter your delivery address');
      return;
    }
    if (!city.trim()) {
      toast.warning('Please enter your city');
      return;
    }
    setStep(3);
  };

  const handlePaymentMethodSelect = (method) => {
    setPaymentMethod(method);
    if (method === 'cod') {
      setPaymentProof('');
      setProofPreview('');
    }
  };

  const handleProofChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image of your payment screenshot');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Image must be under 4MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPaymentProof(reader.result);
      setProofPreview(reader.result);
      toast.success('Payment screenshot attached');
    };
    reader.readAsDataURL(file);
  };

  const handlePlaceOrder = async () => {
    if (!cart || cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (paymentMethod === 'online' && !paymentProof) {
      toast.warning('Please upload a screenshot of your payment before placing the order');
      return;
    }

    setIsProcessing(true);
    try {
      const items = cart.map((item) => ({
        productId: item.product_id,
        quantity: item.quantity,
        price: item.price,
      }));

      const response = await fetch(`${API_BASE_URL}?action=create_order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          items,
          total: parseFloat(total),
          currency: PAYMENT_CURRENCY,
          paymentMethod,
          paymentProof: paymentMethod === 'online' ? paymentProof : '',
          shippingMethod,
          address: isPickup ? 'Store pickup' : address,
          city: isPickup ? '' : city,
          phone,
          coordinates: latitude && longitude ? `${latitude},${longitude}` : null,
        }),
      });

      const data = await response.json();
      if (data.status === 'ok') {
        setPlacedOrderId(data.orderId);
        setPlacedPaymentStatus(data.paymentStatus || (paymentMethod === 'online' ? 'pending_verification' : 'unpaid'));
        const wa = data.whatsapp || {};
        setWhatsappLinks({
          admin: wa.admin || '',
          customerNotify: wa.customerNotify || '',
        });
        setStep(4);
        toast.success(
          paymentMethod === 'online'
            ? 'Order placed — payment awaits admin verification'
            : 'Order placed successfully'
        );
        // Offer free Click-to-Chat so customer can notify the shop
        if (wa.admin) {
          setTimeout(() => openWhatsAppLink(wa.admin), 600);
        }
      } else {
        toast.error(data.message || 'Failed to place order');
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Order error:', err);
      toast.error('Error placing order. Please try again.');
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <LoginPrompt
        icon="🛍️"
        title="Checkout"
        message="Please login to place your order"
        onLoginClick={onLoginClick}
        onBackClick={onBackClick}
      />
    );
  }

  const mapSrc =
    latitude && longitude
      ? `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - 0.02}%2C${latitude - 0.015}%2C${longitude + 0.02}%2C${latitude + 0.015}&layer=mapnik&marker=${latitude}%2C${longitude}`
      : 'https://www.openstreetmap.org/export/embed.html?bbox=73.05%2C33.52%2C73.2%2C33.62&layer=mapnik';

  return (
    <div className="checkout-page fade-in">
      <BackButton onClick={onBackClick} label="Back to Cart" />
      <div className="page-title-header">
        <h1>Checkout</h1>
      </div>

      {step === 4 ? (
        <div className="success-container fade-in">
          <div className="success-message">
            <div className="success-icon">
              <FontAwesomeIcon icon={faCheck} />
            </div>
            <h2>Order Placed Successfully!</h2>
            <p className="order-id">Order ID: #{placedOrderId || '—'}</p>
            <div className="order-status-pipeline">
              <span className="pipe-step active">Placed</span>
              <span className="pipe-line" />
              <span className="pipe-step">Shipped</span>
              <span className="pipe-line" />
              <span className="pipe-step">Delivered</span>
            </div>
            <p>Thank you for your order. You can track status anytime in My Orders.</p>
            <div className="order-details">
              {isPickup ? (
                <p>
                  <strong>Shipping:</strong> Store pickup
                </p>
              ) : (
                <p>
                  <strong>Delivery Address:</strong> {address}, {city}
                </p>
              )}
              <p>
                <strong>Phone:</strong> {phone}
              </p>
              <p>
                <strong>Payment:</strong>{' '}
                {paymentMethod === 'cod'
                  ? 'Cash on Delivery'
                  : placedPaymentStatus === 'pending_verification'
                    ? 'Online — pending admin verification'
                    : 'Online Payment'}
              </p>
              <p>
                <strong>Total:</strong> {formatMoney(total)} {PAYMENT_CURRENCY}
              </p>
            </div>

            {(whatsappLinks.admin || whatsappLinks.customerNotify) && (
              <div className="whatsapp-cta-box">
                <p className="whatsapp-cta-hint">
                  Free WhatsApp: open the chat and tap <strong>Send</strong> (not automatic).
                </p>
                {whatsappLinks.admin ? (
                  <a
                    className="btn-whatsapp"
                    href={whatsappLinks.admin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span aria-hidden="true">💬</span> Notify shop on WhatsApp
                  </a>
                ) : null}
              </div>
            )}

            <button
              className="btn-primary"
              onClick={() => {
                if (onSuccess) onSuccess();
              }}
            >
              Go to My Orders
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="progress-indicator">
            <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-label">Details</div>
            </div>
            {!isPickup && (
              <>
                <div className={`progress-line ${step >= 2 ? 'active' : ''}`} />
                <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
                  <div className="step-number">2</div>
                  <div className="step-label">Address</div>
                </div>
              </>
            )}
            <div className={`progress-line ${step >= 3 ? 'active' : ''}`} />
            <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
              <div className="step-number">{isPickup ? 2 : 3}</div>
              <div className="step-label">Payment</div>
            </div>
          </div>

          <div className="checkout-container">
            <div className="checkout-main">
              {step === 1 && (
                <div className="step-section fade-in">
                  <h2>Your Details</h2>
                  {isPickup && (
                    <div className="pickup-banner">
                      <FontAwesomeIcon icon={faStore} />
                      <span>Store pickup selected — collect from the nearest branch.</span>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Name</label>
                    <input type="text" value={user?.name || ''} disabled className="form-input disabled" />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" value={user?.email || ''} disabled className="form-input disabled" />
                  </div>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="03XX XXXXXXX"
                      className="form-input"
                    />
                  </div>
                  <button className="btn-primary" onClick={handleStep1Continue}>
                    {isPickup ? 'Continue to Payment' : 'Continue to Address'}
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="step-section fade-in">
                  <h2>Delivery Address</h2>

                  <div className="checkout-map-wrap" ref={mapRef}>
                    <iframe title="Delivery map" className="checkout-map" src={mapSrc} loading="lazy" />
                  </div>

                  <button className="btn-location" onClick={getLocation} disabled={locating}>
                    <FontAwesomeIcon icon={locating ? faSpinner : faLocationCrosshairs} spin={locating} />
                    {locating ? ' Fetching address…' : ' Use Current Location'}
                  </button>

                  <div className="form-group">
                    <label>Address *</label>
                    <textarea
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Street, building, area"
                      className="form-textarea"
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="City"
                      className="form-input"
                    />
                  </div>
                  {latitude && longitude && (
                    <div className="location-info">
                      <FontAwesomeIcon icon={faMapMarkerAlt} /> Map pin set — address resolved from your location
                    </div>
                  )}
                  <button className="btn-primary" onClick={handleStep2Continue}>
                    Continue to Payment
                  </button>
                </div>
              )}

              {step === 3 && (
                <div className="step-section fade-in">
                  <h2>Payment Method</h2>
                  <p className="payment-hint">
                    Choose Cash on Delivery, or pay online by transferring{' '}
                    <strong>{formatMoney(total)}</strong> and uploading your payment screenshot.
                  </p>

                  <div className="payment-options">
                    <button
                      type="button"
                      className={`payment-option ${paymentMethod === 'cod' ? 'active' : ''}`}
                      onClick={() => handlePaymentMethodSelect('cod')}
                    >
                      <div className="payment-icon">💵</div>
                      <div className="payment-details">
                        <h3>Cash on Delivery</h3>
                        <p>Pay in cash when your order arrives</p>
                      </div>
                      <span className="payment-radio" aria-hidden="true" />
                    </button>

                    <button
                      type="button"
                      className={`payment-option ${paymentMethod === 'online' ? 'active' : ''}`}
                      onClick={() => handlePaymentMethodSelect('online')}
                    >
                      <div className="payment-icon">
                        <FontAwesomeIcon icon={faUniversity} />
                      </div>
                      <div className="payment-details">
                        <h3>Online Payment</h3>
                        <p>Bank transfer, EasyPaisa or JazzCash — then upload proof</p>
                      </div>
                      <span className="payment-radio" aria-hidden="true" />
                    </button>
                  </div>

                  {paymentMethod === 'online' && (
                    <div className="payment-instructions online-pay-panel">
                      <h4>How to pay online</h4>
                      <ol className="online-pay-steps">
                        <li>
                          Open your bank / <strong>EasyPaisa</strong> / <strong>JazzCash</strong> app
                        </li>
                        <li>
                          Send <strong>{formatMoney(total)}</strong> to one of the accounts below
                        </li>
                        <li>Take a screenshot of the successful payment</li>
                        <li>Upload the screenshot here, then place your order</li>
                        <li>Admin will verify the image and mark your order as Paid</li>
                      </ol>

                      <div className="account-cards">
                        <div className="account-card">
                          <div className="account-card-head">
                            <FontAwesomeIcon icon={faUniversity} />
                            <h5>Bank account</h5>
                          </div>
                          <div className="account-row">
                            <span>Account title</span>
                            <strong>{BANK_ACCOUNT.accountTitle}</strong>
                          </div>
                          <div className="account-row">
                            <span>IBAN</span>
                            <strong>{BANK_ACCOUNT.iban || '—'}</strong>
                            {BANK_ACCOUNT.iban ? (
                              <button
                                type="button"
                                className="btn-copy-sm"
                                onClick={() => copyText('IBAN', BANK_ACCOUNT.iban, toast)}
                              >
                                <FontAwesomeIcon icon={faCopy} />
                              </button>
                            ) : null}
                          </div>
                          <div className="account-row">
                            <span>Account number</span>
                            <strong>{BANK_ACCOUNT.accountNumber || '—'}</strong>
                            {BANK_ACCOUNT.accountNumber ? (
                              <button
                                type="button"
                                className="btn-copy-sm"
                                onClick={() =>
                                  copyText('Account number', BANK_ACCOUNT.accountNumber, toast)
                                }
                              >
                                <FontAwesomeIcon icon={faCopy} />
                              </button>
                            ) : null}
                          </div>
                          <div className="account-row">
                            <span>Bank name</span>
                            <strong>{BANK_ACCOUNT.bankName || '—'}</strong>
                          </div>
                        </div>

                        <div className="account-card">
                          <div className="account-card-head">
                            <FontAwesomeIcon icon={faMobileAlt} />
                            <h5>EasyPaisa / JazzCash</h5>
                          </div>
                          <div className="account-row">
                            <span>Account title</span>
                            <strong>{WALLET_ACCOUNT.accountTitle}</strong>
                          </div>
                          <div className="account-row">
                            <span>Account number</span>
                            <strong>{WALLET_ACCOUNT.accountNumber || '—'}</strong>
                            {WALLET_ACCOUNT.accountNumber ? (
                              <button
                                type="button"
                                className="btn-copy-sm"
                                onClick={() =>
                                  copyText('Wallet number', WALLET_ACCOUNT.accountNumber, toast)
                                }
                              >
                                <FontAwesomeIcon icon={faCopy} />
                              </button>
                            ) : null}
                          </div>
                          <div className="wallet-logos-row">
                            <img src="/images/easypaisa.jpeg" alt="EasyPaisa" />
                            <img src="/images/jazzcash.png" alt="JazzCash" />
                          </div>
                        </div>
                      </div>

                      <div className="proof-upload">
                        <label className="proof-upload-label">Payment screenshot</label>
                        <input
                          ref={proofInputRef}
                          type="file"
                          accept="image/*"
                          className="proof-file-input"
                          onChange={handleProofChange}
                        />
                        <button
                          type="button"
                          className="btn-upload-proof"
                          onClick={() => proofInputRef.current && proofInputRef.current.click()}
                        >
                          <FontAwesomeIcon icon={faUpload} />{' '}
                          {proofPreview ? 'Change screenshot' : 'Upload payment screenshot'}
                        </button>
                        {proofPreview ? (
                          <div className="proof-preview-wrap">
                            <img src={proofPreview} alt="Payment proof" className="proof-preview" />
                          </div>
                        ) : (
                          <p className="proof-required-hint">
                            Screenshot required before you can place an online order.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="order-review">
                    <h3>Order Summary (PKR)</h3>
                    <div className="review-item">
                      <span>Subtotal ({cart.length} items):</span>
                      <span>{formatMoney(total)}</span>
                    </div>
                    <div className="review-item">
                      <span>Shipping:</span>
                      <span>Free</span>
                    </div>
                    <div className="review-item total">
                      <span>Total (PKR):</span>
                      <span>{formatMoney(total)}</span>
                    </div>
                  </div>

                  <button
                    className="btn-primary btn-large"
                    onClick={handlePlaceOrder}
                    disabled={isProcessing || (paymentMethod === 'online' && !paymentProof)}
                  >
                    {isProcessing
                      ? 'Placing Order…'
                      : paymentMethod === 'cod'
                        ? 'Place Order (Cash on Delivery)'
                        : paymentProof
                          ? 'Place Order (Awaiting verification)'
                          : 'Upload screenshot to continue'}
                  </button>
                </div>
              )}
            </div>

            <div className="order-sidebar">
              <h3>Order Summary</h3>
              <div className="sidebar-items">
                {cart.map((item) => (
                  <div key={item.product_id} className="sidebar-item">
                    <img src={item.image_data || item.image} alt={item.name} />
                    <div className="item-info">
                      <p className="item-name">{item.name}</p>
                      <p className="item-qty">Qty: {item.quantity}</p>
                    </div>
                    <p className="item-price">{formatMoney(item.price * item.quantity)}</p>
                  </div>
                ))}
              </div>
              <div className="sidebar-total">
                <strong>Total (PKR):</strong>
                <strong>{formatMoney(total)}</strong>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CheckoutPage;
